/**
 * ログインページ（パスワード認証対応版）
 *
 * 【改修前との違い】
 *
 * 改修前:
 * - useAuth().login() を呼ぶ → AuthContext 内で fetchEmployeeByCode()
 * - パスワードは無視されていた（_password）
 *
 * 改修後:
 * - RTK Query の useLoginMutation() を直接使う
 * - POST /auth/login に { code, password } を送信
 * - サーバーがパスワードを bcrypt で検証
 * - 成功 → Cookie 自動設定 + Redux にユーザー情報を保存
 * - 429 ロックアウト対応（5回失敗でカウントダウン表示）
 * - must_change_password → パスワード変更画面へリダイレクト
 */

import { useState, useCallback, useEffect, useRef } from "react";
import { recordUserAction } from "../utils/awsRum";
import { useNavigate } from "react-router-dom";
import circuitPlannerLogo from "../assets/images/circuit_planner_logo.png";
import logoAutorace from "../assets/images/logo_autorace.png";
import { useLoginMutation } from "../shared/api/endpoints/authApi";
import { useAppDispatch } from "../app/store";
import { setUser } from "../shared/store/slices/authSlice";
import { useAuth } from "../contexts/AuthContext";

/**
 * ロックアウトカウントダウンコンポーネント
 *
 * 【これは何？】
 * ログインに5回失敗すると、サーバーが 429 レスポンスを返す。
 * レスポンスには retry_after_seconds（何秒後に再試行できるか）が含まれる。
 * このコンポーネントは残り秒数をカウントダウン表示する。
 *
 * 【なぜ必要？】
 * ブルートフォース攻撃（パスワードを総当たりで試す攻撃）への対策。
 * 一定時間ロックすることで、攻撃者が大量のパスワードを試せなくなる。
 */
const LockoutCountdown = ({ seconds, onExpire }: { seconds: number; onExpire: () => void }) => {
    const [remaining, setRemaining] = useState(seconds);
    const onExpireRef = useRef(onExpire);
    onExpireRef.current = onExpire;

    useEffect(() => {
        setRemaining(seconds);
    }, [seconds]);

    useEffect(() => {
        if (remaining <= 0) {
            onExpireRef.current();
            return;
        }
        const timer = setInterval(() => {
            setRemaining((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    onExpireRef.current();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, [remaining]);

    const minutes = Math.floor(remaining / 60);
    const secs = remaining % 60;

    return (
        <div className="rounded-lg bg-warning-50 border border-warning-200 p-3">
            <p className="text-sm text-warning-700">
                ログイン試行回数を超えました。{minutes}分{secs.toString().padStart(2, "0")}秒後に再試行できます。
            </p>
        </div>
    );
};

/** RTK Query のエラーレスポンス型 */
interface ApiErrorResponse {
    status: number;
    data: {
        detail: string;
        retry_after_seconds?: number;
    };
}

/** RTK Query のエラー型ガード */
function isApiError(error: unknown): error is ApiErrorResponse {
    return (
        typeof error === "object" &&
        error !== null &&
        "status" in error &&
        typeof (error as ApiErrorResponse).status === "number"
    );
}

const LoginPage = () => {
    const { isAuthenticated, isLoading: authLoading } = useAuth();
    const navigate = useNavigate();

    // 既に認証済みならホームへリダイレクト
    useEffect(() => {
        if (!authLoading && isAuthenticated) {
            navigate("/home", { replace: true });
        }
    }, [authLoading, isAuthenticated, navigate]);

    const [userId, setUserId] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [retryAfter, setRetryAfter] = useState<number | null>(null);
    const [showForgotPassword, setShowForgotPassword] = useState(false);

    /**
     * useLoginMutation() — RTK Query が自動生成した hook
     *
     * 返り値:
     * - login: API を呼ぶ関数（trigger）
     * - isLoading: API 呼び出し中かどうか
     *
     * 使い方:
     * const result = await login({ code, password }).unwrap();
     * - .unwrap() を付けると、エラー時に例外を throw する（try/catch で扱える）
     * - .unwrap() を付けないと、result.data / result.error で判定する
     */
    const [login, { isLoading }] = useLoginMutation();
    const dispatch = useAppDispatch();

    /**
     * フォーム送信ハンドラ
     *
     * 【処理フロー】
     * 1. POST /auth/login に { code, password } を送信
     * 2. 成功:
     *    a. サーバーが Set-Cookie でトークンを設定（フロントは何もしない）
     *    b. レスポンスの user を Redux Store に保存
     *    c. must_change_password=true → パスワード変更画面へ
     *    d. それ以外 → ホーム画面へ
     * 3. 失敗:
     *    a. 401 → 「スタッフコードまたはパスワードが正しくありません」
     *    b. 429 → ロックアウトカウントダウン表示
     */
    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");
        setRetryAfter(null);
        recordUserAction("login_submitted");

        if (!userId || !password) {
            setError("IDとパスワードを入力してください");
            recordUserAction("validation_error", { form: "login", field: "credentials", reason: "empty_fields" });
            return;
        }

        try {
            const result = await login({ code: userId, password }).unwrap();

            // ログイン成功 → ユーザー情報を Redux Store に保存
            dispatch(setUser(result.user));

            // 初回パスワード変更が必要な場合はパスワード変更画面へ
            if (result.user.must_change_password) {
                navigate("/auth/change-password", { replace: true });
            } else {
                navigate("/home", { replace: true });
            }
        } catch (err) {
            if (isApiError(err)) {
                if (err.status === 429) {
                    // ロックアウト（5回失敗）
                    setError(err.data.detail);
                    setRetryAfter(err.data.retry_after_seconds ?? 900);
                } else if (err.status === 401) {
                    // 認証失敗
                    setError(err.data.detail);
                } else {
                    setError("ログイン処理中にエラーが発生しました");
                }
            } else {
                setError("ログイン処理中にエラーが発生しました");
            }
            recordUserAction("validation_error", { form: "login", field: "credentials", reason: "auth_failed" });
        }
    }, [userId, password, login, dispatch, navigate]);

    const isLocked = retryAfter !== null && retryAfter > 0;

    return (
        <div className="min-h-svh relative overflow-hidden flex flex-col items-center">
            {/* 2トーン背景 */}
            <div className="absolute inset-x-0 top-0 h-[43%] bg-gray-50" />
            <div className="absolute inset-x-0 bottom-0 h-[57%] bg-[#B8922A]" />

            {/* コンテンツ */}
            <div className="relative z-10 flex flex-col items-center w-full px-4 pt-12 pb-8">
                {/* ロゴエリア */}
                <div className="text-center mb-8">
                    <img
                        src={logoAutorace}
                        alt="Auto Race"
                        className="h-20 mx-auto"
                    />
                    <img
                        src={circuitPlannerLogo}
                        alt="Circuit Planner"
                        className="h-7 mt-2 mx-auto"
                    />
                </div>

                {/* ログインカード */}
                <div className="w-full max-w-md bg-white rounded-2xl px-8 py-8 shadow-[0_4px_16px_2px_rgba(184,146,42,0.2)]">
                    <h1 className="text-center text-xl font-semibold text-[#B8922A] mb-6">ログイン</h1>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* ID */}
                        <div className="flex flex-col gap-1">
                            <label className="text-sm font-medium text-gray-700">ID</label>
                            <input
                                type="text"
                                placeholder="IDを入力してください"
                                value={userId}
                                onChange={(e) => setUserId(e.target.value)}
                                disabled={isLoading || isLocked}
                                autoComplete="username"
                                className="w-full bg-gray-100 rounded-md px-4 py-3 text-base placeholder-gray-400 outline-none shadow-inner focus:ring-2 focus:ring-[#B8922A] disabled:opacity-60 disabled:cursor-not-allowed"
                            />
                        </div>

                        {/* パスワード */}
                        <div className="flex flex-col gap-1">
                            <label className="text-sm font-medium text-gray-700">パスワード</label>
                            <input
                                type="password"
                                placeholder="パスワードを入力してください"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                disabled={isLoading || isLocked}
                                autoComplete="current-password"
                                className="w-full bg-gray-100 rounded-md px-4 py-3 text-base placeholder-gray-400 outline-none shadow-inner focus:ring-2 focus:ring-[#B8922A] disabled:opacity-60 disabled:cursor-not-allowed"
                            />
                        </div>

                        {/* エラーメッセージ */}
                        {error && !isLocked && (
                            <div className="rounded-lg bg-error-50 border border-error-200 p-3">
                                <p className="text-sm text-error-700">{error}</p>
                            </div>
                        )}

                        {/* ロックアウトカウントダウン */}
                        {isLocked && (
                            <LockoutCountdown
                                seconds={retryAfter}
                                onExpire={() => setRetryAfter(null)}
                            />
                        )}

                        {/* ログインボタン */}
                        <div className="pt-2">
                            <button
                                type="submit"
                                disabled={isLoading || isLocked}
                                className="w-full bg-[#B8922A] text-white py-3 rounded-md font-medium text-base hover:bg-[#A07820] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {isLoading ? "ログイン中..." : "ログイン"}
                            </button>
                        </div>

                        {/* パスワードを忘れた方はこちら */}
                        {/*
                          TODO: 仮パスワード発行の仕様が決まり次第、下記に置き換える:
                          <Link to="/forgot-password" className="block text-center text-sm text-[#B8922A]">
                              パスワードを忘れた方はこちら
                          </Link>
                        */}
                        <div className="text-center">
                            <button
                                type="button"
                                onClick={() => setShowForgotPassword(true)}
                                className="text-sm text-[#B8922A] hover:underline"
                            >
                                パスワードを忘れた方はこちら
                            </button>
                            {showForgotPassword && (
                                <div className="mt-2 rounded-lg bg-error-50 border border-error-200 p-3 text-left">
                                    <p className="text-sm text-error-700">
                                        パスワードをお忘れの場合は、管理者にお問い合わせください。
                                    </p>
                                </div>
                            )}
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};


export default LoginPage;
